import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, Check, AlertTriangle, Clock, Home, Mail, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { enablePush, triggerNotify } from "@/lib/push";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const ICONS = { rent_overdue: AlertTriangle, rent_due: Clock, contract_expiring: Clock, vacant: Home, fiscal_deadline: Landmark };
const COLORS = { danger: "text-[hsl(var(--destructive))]", warning: "text-[hsl(var(--warning))]", info: "text-muted-foreground" };

export default function NotificationBell() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [pushOn, setPushOn] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
  });

  const markPaid = useMutation({
    mutationFn: ({ cid, period }) => api.post(`/contracts/${cid}/mark-paid?period=${period}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("OK");
    },
  });

  const doEnablePush = async () => {
    try {
      await enablePush();
      setPushOn(true);
      toast.success(t("enable_push") + " ✓");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const doNotify = async () => {
    try {
      const res = await triggerNotify();
      toast.success(`${res.sent} → ${res.alerts} alert(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const doEmail = async () => {
    try {
      const res = await api.post("/notifications/email");
      toast.success(`✉ ${res.data.alerts}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" && !detail.includes("<") ? detail : t("send_email") + " — Resend domain not verified";
      toast.error(msg);
    }
  };

  const count = notes.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" data-testid="notif-bell" className="rounded-sm relative">
          {count > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          {count > 0 && (
            <span
              data-testid="notif-count"
              className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-[hsl(var(--destructive))] text-white text-[10px] flex items-center justify-center"
            >
              {count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-popover w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-display text-lg">{t("notifications")}</span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={doEnablePush} data-testid="enable-push-btn" className="h-7 text-xs">
              {pushOn ? "✓" : t("enable_push")}
            </Button>
            <Button variant="ghost" size="sm" onClick={doNotify} data-testid="notify-me-btn" className="h-7 text-xs">
              {t("notify_me")}
            </Button>
            <Button variant="ghost" size="icon" onClick={doEmail} data-testid="email-me-btn" className="h-7 w-7" title={t("send_email")}>
              <Mail className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {count === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">{t("no_notifications")}</p>
          ) : (
            notes.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <div key={n.id} data-testid={`notif-${n.id}`} className="flex items-start gap-3 px-4 py-3 border-b border-border last:border-0">
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${COLORS[n.severity]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{n.message}</p>
                  </div>
                  {(n.type === "rent_due" || n.type === "rent_overdue") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`mark-paid-${n.contract_id}`}
                      onClick={() => markPaid.mutate({ cid: n.contract_id, period: n.period })}
                      className="h-7 w-7 shrink-0 text-[hsl(var(--success))]"
                      title={t("mark_paid")}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
